/**
 * Call ringtone for web, including iPhone Safari.
 *
 * Safari rule: unmuted play() is allowed only after a user gesture, and only
 * on that same HTMLAudioElement. After getUserMedia (Answer), Safari revokes
 * that permission until the next tap.
 *
 * So this module does exactly three things:
 * 1. Keep one incoming Audio and one outgoing Audio for the life of the page.
 * 2. On any tap (when not in a live call), mute-prime those same elements.
 * 3. On incoming/outgoing, play() that element. On stop, pause it. Never
 *    destroy it, never play a second silent/beep/notification track.
 *
 * CallManager is the only owner of start/stop. Background ringing is FCM,
 * not this file.
 */

import incomingRingtoneMp3 from '../../../assets/ringtones/incoming-call/ES_User Interface, Alert, Notification, Email, Receive, Incoming 05 - Epidemic Sound.mp3';
import outgoingRingtoneMp3 from '../../../assets/ringtones/outgoing-call/ES_User Interface, Alert, Notification, Email, Receive, Incoming 06 - Epidemic Sound.mp3';
import {
  endCallAudioEpoch,
  isCallAudioEpochActive,
  setAmbientAudioSession,
  setRingtoneAudioSession,
} from '../../utils/audioSession';
import { exitCallAudioSession } from './callAudioSessionPolicy';

export const CALL_RINGTONE_VARIANT = {
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
};

const RINGTONE_SRC = {
  [CALL_RINGTONE_VARIANT.INCOMING]: incomingRingtoneMp3,
  [CALL_RINGTONE_VARIANT.OUTGOING]: outgoingRingtoneMp3,
};

const VARIANTS = [CALL_RINGTONE_VARIANT.INCOMING, CALL_RINGTONE_VARIANT.OUTGOING];

class CallRingtoneService {
  constructor() {
    /** @type {Record<string, HTMLAudioElement>} */
    this.players = {};
    /** @type {'incoming'|'outgoing'|null} */
    this.activeVariant = null;
    /** True when the last play() was blocked by autoplay. */
    this.awaitingGesture = false;
    this.initialized = false;
    this.playToken = 0;
    /** @type {Set<() => void>} */
    this.listeners = new Set();
  }

  notify() {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (_) { }
    });
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  ensureHost() {
    if (typeof document === 'undefined' || !document.body) return null;
    let host = document.getElementById('jh-call-ringtone-host');
    if (host) return host;
    host = document.createElement('div');
    host.id = 'jh-call-ringtone-host';
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText =
      'position:fixed;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none';
    document.body.appendChild(host);
    return host;
  }

  getPlayer(variant) {
    if (this.players[variant]) return this.players[variant];

    // Mute before assigning src — iPhone can resume the last HTMLAudio src on construct.
    const audio = new Audio();
    audio.autoplay = false;
    audio.muted = true;
    audio.volume = 0;
    audio.preload = 'auto';
    audio.loop = true;
    audio.playsInline = true;
    audio.setAttribute('playsinline', '');
    audio.setAttribute('webkit-playsinline', '');
    try {
      audio.disableRemotePlayback = true;
    } catch (_) { }
    audio.src = RINGTONE_SRC[variant];

    audio.addEventListener('playing', () => {
      const isActivePlayer = this.activeVariant && this.players[this.activeVariant] === audio;
      if (isActivePlayer) return;
      this.silencePlayer(audio);
    });

    const host = this.ensureHost();
    if (host) host.appendChild(audio);
    this.players[variant] = audio;
    return audio;
  }

  preload() {
    VARIANTS.forEach((variant) => this.getPlayer(variant));
  }

  silencePlayer(audio) {
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch (_) { }
    audio.muted = true;
    audio.volume = 0;
  }

  stopPlayer(variant) {
    this.silencePlayer(this.players[variant]);
  }

  /**
   * iPhone Safari restores the last HTMLAudio src after reload / bfcache.
   * Pause immediately so a hard refresh never replays one ring.
   */
  silenceRestoredPlayback() {
    if (this.activeVariant) return;
    VARIANTS.forEach((variant) => this.stopPlayer(variant));
    try {
      if (typeof navigator !== 'undefined' && navigator.mediaSession) {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
      }
    } catch (_) { }
  }

  /**
   * Mute-prime the real ringtone elements from a user gesture.
   * iOS unlocks per element — a silent WAV on a different node does not help.
   */
  primeFromGesture() {
    if (typeof window === 'undefined') return;
    if (isCallAudioEpochActive()) return;

    this.preload();
    VARIANTS.forEach((variant) => {
      const audio = this.getPlayer(variant);
      if (this.activeVariant === variant) return;
      try {
        audio.muted = true;
        audio.volume = 0;
        const playResult = audio.play();
        const finish = () => {
          if (this.activeVariant === variant) return;
          this.silencePlayer(audio);
        };
        if (playResult?.then) playResult.then(finish).catch(finish);
        else finish();
      } catch (_) {
        this.silencePlayer(audio);
      }
    });
  }

  unlockFromGesture() {
    if (this.activeVariant) {
      this.playActiveFromGesture();
      return;
    }
    this.primeFromGesture();
  }

  isPlaying(variant) {
    const audio = this.players[variant];
    return Boolean(audio && !audio.paused && !audio.ended && !audio.muted);
  }

  playActiveFromGesture() {
    const variant = this.activeVariant;
    if (!variant) return;
    this.awaitingGesture = false;
    this.playToken += 1;
    const token = this.playToken;
    const audio = this.getPlayer(variant);
    setRingtoneAudioSession();
    audio.loop = true;
    audio.muted = false;
    audio.volume = 1;
    try {
      audio.currentTime = 0;
    } catch (_) { }
    const playResult = audio.play();
    if (playResult?.then) {
      playResult
        .then(() => {
          if (this.playToken !== token) return;
          this.awaitingGesture = false;
          this.notify();
        })
        .catch(() => {
          if (this.playToken !== token) return;
          this.awaitingGesture = true;
          this.notify();
        });
    }
    this.notify();
  }

  /** @param {'incoming'|'outgoing'} variant */
  start(variant) {
    if (typeof window === 'undefined') return;

    if (this.activeVariant && this.activeVariant !== variant) {
      this.stopPlayer(this.activeVariant);
    }

    this.activeVariant = variant;
    this.awaitingGesture = false;
    this.playToken += 1;
    const token = this.playToken;

    if (variant === CALL_RINGTONE_VARIANT.INCOMING) {
      endCallAudioEpoch();
    }
    setRingtoneAudioSession();

    const audio = this.getPlayer(variant);
    audio.loop = true;
    audio.muted = false;
    audio.volume = 1;
    try {
      audio.currentTime = 0;
    } catch (_) { }

    const playResult = audio.play();
    if (playResult?.then) {
      playResult
        .then(() => {
          if (this.playToken !== token || this.activeVariant !== variant) return;
          this.awaitingGesture = false;
          this.notify();
        })
        .catch(() => {
          if (this.playToken !== token || this.activeVariant !== variant) return;
          this.awaitingGesture = true;
          this.notify();
        });
    }
    this.notify();
  }

  /**
   * @param {{ restoreSession?: boolean, variant?: 'incoming'|'outgoing'|null }} [options]
   */
  stop({ restoreSession = false, variant = null } = {}) {
    if (variant && this.activeVariant && this.activeVariant !== variant) {
      return;
    }

    this.playToken += 1;
    this.activeVariant = null;
    this.awaitingGesture = false;
    VARIANTS.forEach((item) => this.stopPlayer(item));

    if (restoreSession && !isCallAudioEpochActive()) {
      exitCallAudioSession();
    } else if (!isCallAudioEpochActive()) {
      setAmbientAudioSession();
    }
    this.notify();
  }

  init() {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    const boot = () => {
      this.preload();
      this.silenceRestoredPlayback();

      const onGesture = () => this.unlockFromGesture();
      const opts = { capture: true, passive: true };
      document.addEventListener('pointerdown', onGesture, opts);
      document.addEventListener('touchstart', onGesture, opts);
      document.addEventListener('click', onGesture, opts);

      const onPageHide = () => this.silenceRestoredPlayback();
      const onPageShow = () => this.silenceRestoredPlayback();
      window.addEventListener('pagehide', onPageHide);
      window.addEventListener('pageshow', onPageShow);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.silenceRestoredPlayback();
        }
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
      boot();
    }
  }
}

export const callRingtoneService = new CallRingtoneService();

export function initCallRingtoneAudioUnlock() {
  callRingtoneService.init();
}

export function unlockCallRingtoneAudio() {
  callRingtoneService.unlockFromGesture();
}

export function kickUnlockCallRingtoneFromUserGesture() {
  callRingtoneService.unlockFromGesture();
}

export function boostCallRingtoneFromUserGesture() {
  callRingtoneService.unlockFromGesture();
}

export function isCallRingtoneAudioUnlocked() {
  return !callRingtoneService.awaitingGesture;
}

export function isCallRingtoneAwaitingGesture() {
  return Boolean(
    callRingtoneService.activeVariant && callRingtoneService.awaitingGesture
  );
}

export function subscribeCallRingtoneState(listener) {
  return callRingtoneService.subscribe(listener);
}

export function startCallRingtoneLoop(variant) {
  callRingtoneService.start(variant);
}

/** @param {{ restoreSession?: boolean, variant?: 'incoming'|'outgoing'|null }} [options] */
export function stopCallRingtoneLoop(options) {
  callRingtoneService.stop(options);
}

export function isCallRingtonePlaying(variant) {
  return callRingtoneService.isPlaying(variant);
}

export function vibrateInaudibleFallback() { }

export function cancelCallRingVibration() { }

export function syncCallRingtoneKeepAlive() { }
